/**
 * Slice 5 — `<FallbackSelectContent />` shared dropdown body.
 *
 * Picker components that present databases inside a shadcn `<Select>` use
 * this to render the universal fallback presentation in the dropdown body:
 *   - on `state === 'ok'` → the real database list (`<SelectItem>` per row)
 *   - on `state === 'empty' | 'error'` → the `DatabaseListingFallbackBanner`
 *     followed by `[config.database, ...knownDatabases]` entries each with
 *     an `<UnverifiedDatabaseIndicator>` (compact density).
 *
 * Centralising this means schema-compare, data-compare, table-explorer,
 * common DB selector, security-manager toolbar, and table-designer dialog
 * all render the same affordance with the same wiring.
 *
 * #91 — on `state === 'ok'` it also renders `selectedDatabase` when the
 * listing does not contain it. Radix paints NOTHING (not even the
 * placeholder) for a `<Select>` whose `value` matches no `<SelectItem>`, so
 * without this row every picker holding a since-dropped / no-longer-visible /
 * limit-hidden database goes silently blank. This is the seam: the eight call
 * sites differ only in where their selected value lives, so putting the rule
 * here means a ninth picker cannot reintroduce the blank.
 *
 * The `'error'` / `'empty'` branch is deliberately unchanged — there the
 * banner already tells the user the list is not authoritative, and the
 * fallback entries are built from the connection's own config + `knownDatabases`,
 * which is where a previously-used database is expected to be found anyway.
 */

import { SelectItem } from '../../../components/ui/select';
import { DatabaseListingFallbackBanner } from './DatabaseListingFallbackBanner';
import { UnverifiedDatabaseIndicator } from './UnverifiedDatabaseIndicator';
import { useFallbackPresentation } from '../hooks/useFallbackPresentation';
import { ListingLoadingLabel } from '../../workspace/common/components/ListingLoadingLabel';
import { findDatabaseInList } from '../../connections/utils/connectionDatabaseLimit';
import type { DatabasesListingState } from '../../database-objects/services/databasesService';

type FallbackSelectContentProps = {
  connectionId: string | undefined;
  state: DatabasesListingState | undefined;
  databases: string[];
  isLoading?: boolean;
  /**
   * #91 — the value the surrounding `<Select>` currently holds. Pass it
   * whenever the picker can hold a database it did not just pick from this
   * list (a restored recent, a restored session tab, a connection whose
   * database limit changed underneath it).
   */
  selectedDatabase?: string;
  /** Pickers should pass a stable prefix so all data-testids are scoped. */
  testIdPrefix: string;
  /**
   * Stamps `data-testid={`${optionTestIdPrefix}-${db}`}` on each REAL database
   * row. Only for pickers that already published per-option testids before they
   * moved onto this component (the blueprint "Add linked database" dialog), so
   * the migration keeps their specs working; new pickers don't need it.
   */
  optionTestIdPrefix?: string;
  /** Optional close-the-parent-dropdown hook for `Show details` clicks. */
  beforeOpenDetails?: () => void;
};

export function FallbackSelectContent({
  connectionId,
  state,
  databases,
  isLoading,
  selectedDatabase,
  testIdPrefix,
  optionTestIdPrefix,
  beforeOpenDetails,
}: FallbackSelectContentProps) {
  const fallback = useFallbackPresentation({ connectionId, state });

  if (state === 'error' || state === 'empty') {
    return (
      <div
        className="flex flex-col gap-2 p-2"
        data-testid={`${testIdPrefix}-database-listing-failed`}
      >
        {connectionId ? (
          <DatabaseListingFallbackBanner
            connectionId={connectionId}
            variant="block"
            trigger={state === 'empty' ? 'list_empty' : 'list_failure'}
            beforeOpenDetails={beforeOpenDetails}
            data-testid={`${testIdPrefix}-fallback-banner`}
            showDetailsTestId={`${testIdPrefix}-show-details`}
            sendTestId={`${testIdPrefix}-send-diagnostic-report`}
          />
        ) : null}
        {fallback.entries.length > 0 ? (
          <div
            className="flex flex-col"
            data-testid={`${testIdPrefix}-fallback-entries`}
          >
            {fallback.entries.map((db) => (
              <SelectItem key={db} value={db}>
                <span className="flex items-center gap-1.5">
                  <span>{db}</span>
                  <UnverifiedDatabaseIndicator
                    kind={fallback.isConfig(db) ? 'config' : 'known'}
                    density="compact"
                    data-testid={`${testIdPrefix}-fallback-indicator-${db}`}
                  />
                </span>
              </SelectItem>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  // Soporte C3 fix-before-merge (final-review-waveC-report.md #11): C3
  // un-disabled these triggers so a user can open the dropdown mid-load —
  // but `databases` is still empty at that point, so without this branch
  // the popper below renders nothing (a blank white box that reads as
  // broken, the exact complaint class the wave fixes). Only reachable once
  // `state` is neither `'error'` nor `'empty'` (handled above), i.e. the
  // first load or a load that hasn't failed yet.
  if (isLoading) {
    return (
      <SelectItem value="_loading" disabled data-testid={`${testIdPrefix}-database-listing-loading`}>
        <ListingLoadingLabel target="databases" />
      </SelectItem>
    );
  }

  // #91 — presence is decided case-insensitively with an exact match preferred
  // (`findDatabaseInList`), so a name that IS in the list under the server's own
  // casing does not get a duplicate row here.
  const unlisted =
    selectedDatabase && findDatabaseInList(databases, selectedDatabase) === undefined
      ? selectedDatabase
      : undefined;

  if (databases.length === 0 && !unlisted) {
    return (
      <SelectItem value="_none" disabled>
        No databases found
      </SelectItem>
    );
  }

  return (
    <>
      {unlisted && (
        <SelectItem
          key={unlisted}
          value={unlisted}
          data-testid={`${testIdPrefix}-unlisted-database-entry`}
        >
          <span className="flex items-center gap-1.5">
            <span>{unlisted}</span>
            <UnverifiedDatabaseIndicator
              kind="unlisted"
              density="compact"
              data-testid={`${testIdPrefix}-unlisted-indicator`}
            />
          </span>
        </SelectItem>
      )}
      {databases.map((db) => (
        <SelectItem
          key={db}
          value={db}
          {...(optionTestIdPrefix ? { 'data-testid': `${optionTestIdPrefix}-${db}` } : {})}
        >
          {db}
        </SelectItem>
      ))}
    </>
  );
}
